import React, { useState, useEffect } from "react";
import axios from "axios";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  getStorage,
  ref,
  uploadBytesResumable,
  getDownloadURL,
} from "firebase/storage";
import {
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Typography,
  Button,
  Grid,
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  DialogActions,
  CardActions,
  MenuItem,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteIcon from "@mui/icons-material/Delete";
import { useConcerns } from "../ConcernData/ConcernDataList";
import { useSearchParams } from "react-router-dom";
import BASE_URL from "../../utils/baseUrl";

// Utility function for date formatting
const formatDate = (timestamp) => new Date(timestamp * 1000).toLocaleString();

// Hook to fetch articles with Firebase Auth state handling
const useArticles = () => {
  const [articles, setArticles] = useState([]);

  const fetchArticles = async (filteredConcern) => {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) {
      console.log("User is not signed in");
      setArticles([]);
      return;
    }

    try {
      const token = await user.getIdToken();
      const { data } = await axios.get(`${BASE_URL}/articles`, {
        headers: { Token: token },
      });
      const formattedData = data.map((item) => ({
        ...item,
        updatedAt: formatDate(item.updatedAt),
      }));
      if (filteredConcern) {
        setArticles(
          formattedData.filter((el) => el.concern === filteredConcern)
        );
      } else {
        setArticles(formattedData);
      }
    } catch (error) {
      console.error("Failed to fetch articles:", error);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(getAuth(), () => fetchArticles());
    return unsubscribe;
  }, []);

  return [articles, fetchArticles];
};

// Dialog component for adding or editing articles
const ArticleDialog = ({
  open,
  onClose,
  onSave,
  initialData = {},
  concerns,
}) => {
  const [formData, setFormData] = useState(initialData);
  const [url, setUrl] = useState(initialData.image);
  const [sections, setSections] = useState(
    initialData && initialData.sections ? initialData.sections : []
  );

  useEffect(() => {
    if (open) {
      setFormData(initialData); // Reset form data when dialog opens
      setUrl(initialData?.image);
      setSections(
        initialData && initialData.sections ? initialData.sections : []
      );
    }
  }, [open, initialData]);

  const addSection = () => {
    console.log("Sections: ", sections, initialData, initialData.sections);
    setSections((prevSections) => [
      ...prevSections,
      {
        englishTitle: "",
        banglaTitle: "",
        englishDetails: "",
        banglaDetails: "",
      },
    ]);
  };

  const deleteSection = (index) => {
    setSections(sections.filter((_, i) => i !== index));
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSectionChange = (e, index) => {
    let newSections = [...sections];
    newSections[index] = {
      ...newSections[index],
      [e.target.name]: e.target.value,
    };
    setSections(newSections);
    setFormData({ ...formData, sections: newSections });
  };

  const handleImageChange = (e) => {
    handleUpload(e.target.files[0]);
  };

  const handleUpload = (image) => {
    const storage = getStorage();
    const timestamp = Date.now();
    const storageRef = ref(storage, `articles/${timestamp}`);
    const uploadTask = uploadBytesResumable(storageRef, image);

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        // Progress function ...
      },
      (error) => {
        // Error function ...
        console.log(error);
      },
      () => {
        // Complete function ...
        getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
          console.log("File available at", downloadURL);
          setUrl(downloadURL);
        });
      }
    );
  };

  const handleSubmit = () => {
    onSave({ ...formData, image: url });
    onClose(); // Close dialog after save
  };

  const handleBannerUpload = (image, index) => {
    const storage = getStorage();
    const timestamp = Date.now();
    const storageRef = ref(storage, `articles/banners/${timestamp}`);
    const uploadTask = uploadBytesResumable(storageRef, image);

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        // Progress function ...
      },
      (error) => {
        // Error function ...
        console.log(error);
      },
      () => {
        // Complete function ...
        getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
          let newSections = [...sections];
          newSections[index] = {
            ...newSections[index],
            banner: downloadURL,
          };
          setSections(newSections);
          setFormData((prevFormData) => ({
            ...prevFormData,
            sections: newSections,
          }));
        });
      }
    );
  };

  const handleIconUpload = (icon, index) => {
    const storage = getStorage();
    const timestamp = Date.now();
    const storageRef = ref(storage, `articles/icons/${timestamp}`);
    const uploadTask = uploadBytesResumable(storageRef, icon);

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        // Progress function ...
      },
      (error) => {
        // Error function ...
        console.log(error);
      },
      () => {
        // Complete function ...
        getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
          let newSections = [...sections];
          newSections[index] = {
            ...newSections[index],
            icon: downloadURL,
          };
          setSections(newSections);
          setFormData((prevFormData) => ({
            ...prevFormData,
            sections: newSections,
          }));
        });
      }
    );
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>
        {formData.id ? "Edit Article" : "Add New Article"}
      </DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          margin="dense"
          name="englishTitle"
          label="English Title"
          value={formData.englishTitle || ""}
          onChange={handleChange}
        />
        <TextField
          fullWidth
          margin="dense"
          name="banglaTitle"
          label="Bangla Title"
          value={formData.banglaTitle || ""}
          onChange={handleChange}
        />
        <TextField
          fullWidth
          name="concern"
          label="Concern"
          value={formData.concern || ""}
          select
          onChange={handleChange}
        >
          {[...concerns, { isEnglishTitle: "None", id: null }].map(
            (concern) => (
              <MenuItem key={concern.id} value={concern.id}>
                {concern.isEnglishTitle}
              </MenuItem>
            )
          )}
        </TextField>
        {url && <img src={url} alt="Preview" height={200} />}
        <TextField
          fullWidth
          margin="dense"
          type="file"
          InputLabelProps={{ shrink: true }}
          variant="outlined"
          onChange={handleImageChange}
        />
        <hr />
        {sections &&
          sections.map((section, index) => (
            <div key={`${initialData.id}_section${index}`}>
              <Grid
                container
                sx={{
                  flexDirection: { xs: "column", md: "row" },
                  justifyContent: "space-between",
                }}
              >
                <Typography variant="h5">Section {index + 1}</Typography>
                <Button
                  size="small"
                  color="error"
                  onClick={() => deleteSection(index)}
                  startIcon={<DeleteIcon />}
                >
                  Delete
                </Button>
              </Grid>
              <Grid
                container
                sx={{
                  flexDirection: { xs: "column", md: "row" },
                  justifyContent: "space-between",
                }}
              >
                <TextField
                  margin="dense"
                  type="file"
                  InputLabelProps={{ shrink: true, title: "Icon" }}
                  variant="outlined"
                  label="Section Banner"
                  onChange={(e) => handleBannerUpload(e.target.files[0], index)}
                />
                {section?.banner && (
                  <img src={section.banner} alt="Preview" height={150} />
                )}
              </Grid>
              <Grid
                container
                sx={{
                  flexDirection: { xs: "column", md: "row" },
                  justifyContent: "space-between",
                }}
              >
                <TextField
                  margin="dense"
                  type="file"
                  InputLabelProps={{ shrink: true, title: "Icon" }}
                  variant="outlined"
                  label="Icon"
                  onChange={(e) => handleIconUpload(e.target.files[0], index)}
                />
                {section?.icon && (
                  <img src={section.icon} alt="Preview" height={80} />
                )}
              </Grid>
              <TextField
                fullWidth
                margin="dense"
                name={`englishTitle`}
                label="English Title"
                value={section.englishTitle || ""}
                onChange={(e) => handleSectionChange(e, index)}
              />
              <TextField
                fullWidth
                margin="dense"
                name={`englishDetails`}
                label="English Description"
                value={section.englishDetails || ""}
                onChange={(e) => handleSectionChange(e, index)}
              />
              <TextField
                fullWidth
                margin="dense"
                name={`banglaTitle`}
                label="Bangla Title"
                value={section.banglaTitle || ""}
                onChange={(e) => handleSectionChange(e, index)}
              />
              <TextField
                fullWidth
                margin="dense"
                name={`banglaDetails`}
                label="Bangla Description"
                value={section.banglaDetails || ""}
                onChange={(e) => handleSectionChange(e, index)}
              />
              <hr />
            </div>
          ))}
        <Button
          variant="contained"
          color="primary"
          onClick={() => addSection()}
        >
          Add Section
        </Button>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          disabled={!url || !formData.englishTitle || !formData.banglaTitle}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
// Component for confirm deletion dialog
const DeleteConfirmationDialog = ({ open, onClose, onConfirm }) => (
  <Dialog open={open} onClose={onClose}>
    <DialogTitle>Confirm Deletion</DialogTitle>
    <DialogContent>Are you sure you want to delete this Article?</DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Cancel</Button>
      <Button onClick={onConfirm} color="primary">
        Delete
      </Button>
    </DialogActions>
  </Dialog>
);

const ArticleList = () => {
  const [articles, fetchArticles] = useArticles();
  const [concerns, fetchConcerns] = useConcerns();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [currentArticle, setCurrentArticle] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const filteredConcern = searchParams.get("concern");
    if (articles) {
      fetchArticles(filteredConcern);
    }
  }, [searchParams, concerns]);

  const handleDialogClose = () => setDialogOpen(false);
  const handleDeleteDialogClose = () => setDeleteDialogOpen(false);

  const handleSave = async (formData) => {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) {
      console.log("User is not signed in");
      return;
    }
    try {
      const token = await user.getIdToken();
      if (formData.id) {
        // Update existing article
        await axios.put(`${BASE_URL}/articles/${formData.id}`, formData, {
          headers: { Token: token },
        });
      } else {
        // Create new article
        await axios.post(`${BASE_URL}/articles`, formData, {
          headers: { Token: token },
        });
      }
      // TODO:Refresh articles list
    } catch (error) {
      console.error("Failed to save article:", error);
    } finally {
      fetchArticles();
    }
  };

  const handleDelete = async () => {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) {
      console.log("User is not signed in");
      return;
    }
    try {
      const token = await user.getIdToken();
      await axios.delete(`${BASE_URL}/articles/${currentArticle.id}`, {
        headers: { Token: token },
      });
      console.log("Delete Article:", currentArticle);

      fetchArticles();
    } catch (error) {
      console.error("Error deleting article:", error);
    } finally {
      handleDeleteDialogClose();
    }
  };

  const gridStyle = { display: "flex", width: "20vw" };

  return (
    <div style={{ width: "100%" }}>
      <Grid
        container
        spacing={{ xs: 2, md: 3 }}
        columns={{ xs: 4, sm: 8, md: 12 }}
      >
        {articles.map((article, index) => (
          <Grid item xs={2} sm={4} md={3} key={index} style={gridStyle}>
            <Card style={{width: '100%'}}>
              <CardActionArea
                onClick={() => {
                  setCurrentArticle(article);
                  setDialogOpen(true);
                }}
              >
                <CardMedia
                  component="img"
                  image={article.image}
                  alt={article.isEnglishTitle}
                  style={{ maxHeight: "200px" }}
                />
                <CardContent>
                  <Typography gutterBottom variant="h5">
                    {article.englishTitle}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {
                      concerns.find((c) => c.id === article.concern)
                        ?.isEnglishTitle
                    }
                  </Typography>
                </CardContent>
              </CardActionArea>
              <CardActions>
                <Button
                  size="small"
                  color="error"
                  onClick={() => {
                    setCurrentArticle(article);
                    setDeleteDialogOpen(true);
                  }}
                  startIcon={<DeleteIcon />}
                >
                  Delete
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
        <Grid item xs={2} sm={4} md={3} style={gridStyle}>
          <Card
            onClick={() => {
              setCurrentArticle(null);
              setDialogOpen(true);
            }}
            style={{
              display: "flex",
              width: "100%",
              alignItems: "center",
              justifyContent: "center",
              color: "#484848",
            }}
          >
            <AddCircleOutlineIcon style={{ fontSize: "4rem" }} />
          </Card>
        </Grid>
      </Grid>
      <ArticleDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        onSave={handleSave}
        initialData={currentArticle || {}}
        concerns={concerns}
      />
      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleDeleteDialogClose}
        onConfirm={handleDelete}
      />
    </div>
  );
};

export default ArticleList;
